import { Link } from "react-router";
import { useEffect, useRef } from "react";

import { resolveIdLink, type IdLink } from "@/utils/idLink";

export interface DatapointMenuTarget {
  entityKey: string;
  x: number;
  y: number;
}

interface DatapointContextMenuProps {
  target: DatapointMenuTarget | null;
  idLink: IdLink;
  identifierLabel?: string;
  onDismiss: () => void;
}

/**
 * What each selection-identifier column names, for the menu's wording. An
 * unmapped column falls back to wording that needs no noun, so a new column
 * reads plainly rather than leaking its storage name.
 */
const ENTITY_NOUN: Record<string, string> = {
  subject: "subject",
  subject_id: "subject",
  participant_id: "participant",
  site: "site",
  site_key: "site",
  inv_key: "investigator",
  trial_nct_id: "trial",
};

const MENU_WIDTH = 260;
const MENU_HEIGHT = 76;
const VIEWPORT_MARGIN = 8;
/** How far the pointer may stray from the point before the menu is a mistake. */
const POINT_HOT_RADIUS = 28;
const AWAY_DELAY_MS = 1000;

const clamp = (value: number, max: number) =>
  Math.max(VIEWPORT_MARGIN, Math.min(value, max - VIEWPORT_MARGIN));

/**
 * The menu a clicked plot point opens, anchored at the cursor.
 *
 * A click still selects that identifier across every plot on the screen; the
 * menu is offered alongside, and only for a point that represents exactly one
 * record whose screen declares an `idLink`. It closes as soon as the pointer
 * has been away from both the point and the menu for a moment, so a click that
 * was only meant to select never leaves anything behind.
 */
export const DatapointContextMenu = ({
  target,
  idLink,
  identifierLabel,
  onDismiss,
}: DatapointContextMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const actionRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (!target) return;

    // Focus the action rather than relying on autoFocus, which React does not
    // apply to an anchor: without this the menu is never announced and Escape
    // has nothing to return focus from.
    actionRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDismiss();
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) onDismiss();
    };

    let awayTimer: number | undefined;
    const stayOpen = () => {
      if (awayTimer === undefined) return;
      clearTimeout(awayTimer);
      awayTimer = undefined;
    };
    const onMouseMove = (event: MouseEvent) => {
      const rect = menuRef.current?.getBoundingClientRect();
      const overMenu =
        !!rect &&
        event.clientX >= rect.left - VIEWPORT_MARGIN &&
        event.clientX <= rect.right + VIEWPORT_MARGIN &&
        event.clientY >= rect.top - VIEWPORT_MARGIN &&
        event.clientY <= rect.bottom + VIEWPORT_MARGIN;
      const nearPoint =
        Math.hypot(event.clientX - target.x, event.clientY - target.y) <=
        POINT_HOT_RADIUS;

      if (overMenu || nearPoint) stayOpen();
      else if (awayTimer === undefined) {
        awayTimer = window.setTimeout(onDismiss, AWAY_DELAY_MS);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("mousemove", onMouseMove);
    window.addEventListener("scroll", onDismiss, true);
    window.addEventListener("resize", onDismiss);

    return () => {
      stayOpen();
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("scroll", onDismiss, true);
      window.removeEventListener("resize", onDismiss);
    };
  }, [target, onDismiss]);

  const href = resolveIdLink(idLink, target?.entityKey ?? "");
  if (!target || !href) return null;

  const noun = identifierLabel ? ENTITY_NOUN[identifierLabel] : undefined;

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label={noun ? `${noun} actions` : "Datapoint actions"}
      data-testid="datapoint-context-menu"
      className="fixed z-50 rounded border border-gray-200 bg-white py-1 shadow-lg"
      style={{
        // Width is fixed rather than content-driven so the clamp below is
        // accurate and the identifier line actually truncates.
        width: MENU_WIDTH,
        left: clamp(target.x, window.innerWidth - MENU_WIDTH),
        top: clamp(target.y, window.innerHeight - MENU_HEIGHT),
      }}
    >
      <div
        role="presentation"
        className="truncate px-3 py-1 text-xs text-gray-500"
      >
        {target.entityKey}
      </div>
      <Link
        ref={actionRef}
        role="menuitem"
        to={href}
        onClick={onDismiss}
        data-testid="datapoint-profile-link"
        className="block px-3 py-1.5 text-sm hover:bg-gray-100"
      >
        {noun ? `Open ${noun} profile` : "Open profile"}
      </Link>
    </div>
  );
};
